import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AuthPage from './components/AuthPage';
import FuturisticChatApp from './components/FuturisticChatApp';
import LandingPage from './components/LandingPage';
import CookieConsent from './components/CookieConsent';
import HealthIndicator from './components/HealthIndicator';
import OfflineIndicator from './components/OfflineIndicator';
import MobileAppWrapper from './components/MobileAppWrapper';
import DownloadAppBanner from './components/DownloadAppBanner';
import AgeVerification from './components/AgeVerification';
import PaymentSuccess from './components/PaymentSuccess';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Page transition variants
const pageTransitionVariants = {
  initial: { 
    opacity: 0, 
    scale: 0.98,
    filter: 'blur(8px)'
  },
  enter: { 
    opacity: 1, 
    scale: 1,
    filter: 'blur(0px)',
    transition: { 
      duration: 0.4, 
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: { 
    opacity: 0, 
    scale: 1.02,
    filter: 'blur(8px)',
    transition: { 
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Loading screen component
const LoadingScreen = () => (
  <motion.div 
    className="fixed inset-0 bg-[#030305] flex items-center justify-center z-50"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div className="text-center">
      <motion.div 
        className="w-16 h-16 mx-auto mb-4 relative"
        animate={{ rotate: 360 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
      >
        <div className="absolute inset-0 border-2 border-[#0F0F16] border-t-[#00F0FF] rounded-full" />
        <motion.div 
          className="absolute inset-1 border-2 border-[#0F0F16] border-t-[#FFE800] rounded-full"
          animate={{ rotate: -360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </motion.div>
      <motion.p 
        className="text-gray-500 text-sm"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        Loading...
      </motion.p>
    </div>
  </motion.div>
);

// Page wrapper component for transitions
const PageWrapper = ({ children }) => (
  <motion.div
    variants={pageTransitionVariants}
    initial="initial"
    animate="enter"
    exit="exit"
    className="min-h-screen"
  >
    {children}
  </motion.div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return user ? children : <Navigate to="/auth" />;
};

function AnimatedRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={
          user ? <Navigate to="/chat" /> : (
            <PageWrapper>
              <LandingPage />
            </PageWrapper>
          )
        } />
        <Route path="/auth" element={
          user ? <Navigate to="/chat" /> : (
            <PageWrapper>
              <AuthPage />
            </PageWrapper>
          )
        } />
        <Route path="/chat" element={
          <PrivateRoute>
            <PageWrapper>
              <FuturisticChatApp />
            </PageWrapper>
          </PrivateRoute>
        } />
        <Route path="/payment-success" element={
          <PrivateRoute>
            <PageWrapper>
              <PaymentSuccess />
            </PageWrapper>
          </PrivateRoute>
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const [ageVerified, setAgeVerified] = useState(false);

  useEffect(() => {
    // Check if already verified
    const verified = localStorage.getItem('age_verified');
    if (verified) {
      setAgeVerified(true);
    }
  }, []);

  return (
    <MobileAppWrapper>
      {/* Age Verification Gate */}
      {!ageVerified && (
        <AgeVerification onVerified={() => setAgeVerified(true)} />
      )}
      
      {ageVerified && (
        <div className="App min-h-screen bg-[#030305]">
          <BrowserRouter>
            <AuthProvider>
              <HealthIndicator />
              <AnimatedRoutes />
              <CookieConsent />
              <OfflineIndicator />
              <DownloadAppBanner />
            </AuthProvider>
          </BrowserRouter>
        </div>
      )}
    </MobileAppWrapper>
  );
}

export default App;